import { useFormatters } from '@/hooks/useFormatters';
import { AU_DIVIDER_SOFT, AU_SUCCESS, AU_TEXT_DIM } from '@/theme/au-compat';
import ECharts from '@features/echarts';
import { defaultTextStyle, defaultTooltip } from '@features/echarts/utils/baseOption';
import { useMovementData } from '@features/gateCommon/hooks/useMovementData';
import { formatPosition } from '@features/gateCommon/utils/formatPosition';
import { isFinalizedState } from '@features/gateCommon/utils/gateMovementState';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Typography, type SxProps, type Theme } from '@mui/material';
import { cyan } from '@mui/material/colors';
import { EChartsOption } from 'echarts';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { PanelHeading, PanelPaper } from './common';

interface Props {
  movementId: number | null;
  /** Ajustes de layout del contenedor — hoy, el tope propio que el panel recupera apilado. */
  sx?: SxProps<Theme>;
}

export const PanelChart: FC<Props> = ({ movementId, sx }) => {
  return (
    // `flex: 1` — el histórico es el que crece: el resumen mide su contenido y lo que sobra de la
    // columna se lo queda el gráfico.
    <PanelPaper
      sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', ...sx }}
    >
      <PanelHeading>
        <Trans>Histórico de posición</Trans>
      </PanelHeading>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Suspense>{movementId && <SuspenseComponent movementId={movementId} />}</Suspense>
      </Box>
    </PanelPaper>
  );
};

/**
 * Cada cuánto avanza el borde derecho del eje de tiempo.
 *
 * El eje existe para que el último tramo de la serie —que con `step: 'end'` es la posición que la
 * compuerta **mantiene**— tenga ancho: con el borde clavado en el último punto, ese tramo mide cero
 * y el valor sostenido no se ve. O sea que hace falta un reloj, pero no uno de un segundo: el eje
 * abarca minutos y un segundo no mueve un pixel, mientras que **cada tick redibuja el gráfico
 * entero** (ver la nota sobre `notMerge` más abajo). A cinco segundos el avance sigue siendo
 * continuo a la vista y los redibujados bajan de 60 a 12 por minuto.
 */
const AXIS_TICK_MS = 5_000;

/**
 * Los colores que esta vista le pasa a ECharts, **resueltos**: el canvas no entiende `var()`, y los
 * valores de `au-compat.ts` ya son literales, así que van directo.
 *
 * El color de la serie es el de la métrica *posición* del registro de dominio de este repo —
 * `metricsDefaults.position` de `echarts/utils/defaultMetricsColors`, que es `cyan`—, y no el de
 * otra métrica ni un hex inventado acá. Se pasa explícito y no por `generateSeriesColors`: ese
 * helper sólo resuelve cuando la serie no trae `color` y hoy no recibe `dataType` (G-008), así que
 * atarlo a él sería otro trabajo.
 */
const METRIC_COLOR = cyan[500];

const SuspenseComponent: FC<{ movementId: number }> = ({ movementId }) => {
  const { movement, gate } = useMovementData(movementId);
  /**
   * El reloj arranca **al montar**, no al cargar el módulo.
   *
   * Era un `const defaultNow = Date.now()` de nivel de módulo: con la app abierta hace rato, el
   * primer render del gráfico de una operación en curso fijaba el borde derecho del eje en el
   * momento en que el bundle se cargó —horas antes que los puntos— y el gráfico salía vacío o
   * invertido hasta el primer tick. Con el tick a cinco segundos (abajo) esa ventana es más larga
   * que cuando era de uno.
   */
  const [now, setNow] = useState(() => Date.now());
  const { formatMetricValue, formatDateTime } = useFormatters();

  /**
   * El reloj corre **sólo mientras la operación está viva**.
   *
   * Terminada, `max` sale de `finishPosition` y `now` no entra en ninguna cuenta — pero el interval
   * seguía latiendo igual, así que un movimiento finalizado abierto en pantalla redibujaba el
   * gráfico una vez por segundo para siempre, sin que ningún dato cambiara.
   */
  const finalized = isFinalizedState(movement.state);

  useEffect(() => {
    if (finalized) return;
    const interval = setInterval(() => {
      setNow(Date.now());
    }, AXIS_TICK_MS);
    return () => clearInterval(interval);
  }, [finalized]);

  /**
   * Los formatters, por ref.
   *
   * `useFormatters` arma **un objeto nuevo en cada render**, con funciones nuevas adentro (no tiene
   * un solo `useMemo`). Tenerlas como dependencias de `option` hacía que `option` cambiara de
   * identidad en cada render de este componente, viniera o no un dato nuevo — y como el `useEffect`
   * de `ECharts` depende de `option`, eso es un `setOption` completo cada vez.
   *
   * La ref se reasigna en el render y no en un effect a propósito: las dos funciones de abajo se
   * llaman durante el `setOption`, que ocurre en un effect posterior, así que ya leen el valor de
   * este render. Lo que no puede pasar es que `option` dependa de identidades inestables.
   */
  const { i18n } = useLingui();
  const formattersRef = useRef({ formatMetricValue, formatDateTime });
  formattersRef.current = { formatMetricValue, formatDateTime };

  const formatPositionValue = useCallback(
    (value: number) => formatPosition(value, formattersRef.current.formatMetricValue),
    [],
  );
  const formatAxisTime = useCallback(
    (value: number | Date) => formattersRef.current.formatDateTime(value),
    [],
  );

  const max = useMemo(() => {
    return movement.finishPosition?.[0] ?? now;
  }, [now, movement.finishPosition]);

  const min = useMemo(() => {
    return movement.startPosition?.[0] ?? (movement.finishPosition?.[0] ?? now) - 10 * 1000;
  }, [now, movement.startPosition, movement.finishPosition]);

  // `null` en una operación manual —la que abre el operario en terreno—: no hubo destino
  // solicitado (AMARU-862), así que no hay línea de referencia que dibujar ni que meter en el eje.
  const requestedPosition = movement.requestedPosition?.[1] ?? null;

  /**
   * El recorrido que el eje muestra: **el configurado del dispositivo, ensanchado si algún dato se
   * sale**. Es el contexto que hace legible el avance —42 → 75 sobre 120 dice cuánto queda, y un
   * eje recortado a los puntos recibidos no—.
   *
   * **Las dos puntas van fijadas, no sólo el techo.** Con `max` puesto y `min` libre, ECharts elige
   * el piso para que los cortes queden en números redondos: con techo 200 y `splitNumber: 2` toma
   * intervalos de 100 y baja el piso a **-100**, que es lo que se veía. Una posición de compuerta no
   * es negativa —es una apertura—, así que el piso es el mínimo configurado y, a falta de él, 0.
   *
   * El ensanchado con los datos no es defensivo de más: si la compuerta reporta 210 sobre un máximo
   * configurado de 200 —descalibración, tope mal medido—, un eje clavado en 200 **recorta la serie
   * y la dibuja plana contra el borde**, o sea que el gráfico miente justo en el caso que hay que
   * ver. Entra también la posición solicitada, que es la `markLine`: fuera de rango, la línea de
   * referencia desaparece sin aviso.
   */
  const { yMin, yMax } = useMemo(() => {
    const configuredMin = gate.dataConfiguration?.minPosition ?? 0;
    const configuredMax = gate.dataConfiguration?.maxPosition;

    /* Recorrido en un `for`, no `Math.min(a, ...values)`.
       El spread pasa un argumento por punto, y el límite de argumentos de una llamada es el stack:
       con una operación lo bastante larga —reproducido con 200.000 posiciones— tira
       `RangeError: Maximum call stack size exceeded` y se lleva el render del gráfico al error
       boundary. No es un límite teórico: `movement.positions` crece con cada mensaje de la
       compuerta y nada lo acota. */
    let dataMin = requestedPosition ?? Infinity;
    let dataMax = requestedPosition ?? -Infinity;
    for (const [, value] of movement.positions) {
      if (value < dataMin) dataMin = value;
      if (value > dataMax) dataMax = value;
    }

    return {
      yMin: Math.min(configuredMin, dataMin),
      // Sin máximo configurado no hay recorrido que mostrar y lo resuelve ECharts con los datos.
      yMax: configuredMax == null ? undefined : Math.max(configuredMax, dataMax),
    };
  }, [movement.positions, requestedPosition, gate.dataConfiguration]);

  const option = useMemo((): EChartsOption => {
    const positions = movement.positions;
    // El binding tiene nombre a propósito: el macro de Lingui arma el placeholder del msgid con la
    // expresión, y una llamada inline lo convertiría en `{0}`.
    const requestedValue = requestedPosition == null ? '' : formatPositionValue(requestedPosition);

    return {
      // La serie crece mientras la compuerta reporta. El otro lado de esto —`notMerge: false` en el
      // `setOption`— lo fija el componente `ECharts` para toda la app, así que acá queda declarado
      // el tiempo y el redibujado completo sigue siendo el de la casa.
      animationDurationUpdate: 500,
      animationEasingUpdate: 'cubicOut',
      grid: {
        id: 'grid',
        bottom: 20,
        top: 25,
        left: 2,
        right: 2,
        containLabel: true,
      },
      tooltip: {
        ...defaultTooltip,
        trigger: 'axis',
      },
      textStyle: defaultTextStyle,
      xAxis: {
        id: 'x',
        type: 'time',
        splitNumber: 2,
        max: max,
        min: min,
        axisLine: {
          lineStyle: {
            color: AU_DIVIDER_SOFT,
          },
        },
        axisLabel: {
          color: AU_TEXT_DIM,
        },
        axisPointer: {
          label: {
            formatter({ value }) {
              if (typeof value === 'number' || value instanceof Date) return formatAxisTime(value);
              return value;
            },
          },
        },
      },
      yAxis: {
        // `boundaryGap` sólo actúa sobre una punta que ECharts decida: con las dos fijadas es
        // inerte, y queda para el caso sin máximo configurado, donde le da aire al techo.
        boundaryGap: ['10%', '10%'],
        id: 'y',
        position: 'right',
        type: 'value',
        splitNumber: 2,
        min: yMin,
        max: yMax,
        splitLine: {
          show: true,
          lineStyle: {
            color: AU_DIVIDER_SOFT,
          },
        },
        axisLabel: {
          color: AU_TEXT_DIM,
          formatter(value) {
            return formatPositionValue(value);
          },
        },
      },
      series: {
        id: 'position',
        xAxisId: 'x',
        yAxisId: 'y',
        name: 'Posición',
        type: 'line',
        // La compuerta reporta una posición y la mantiene hasta el próximo mensaje: entre dos
        // reportes no hay dato, y una línea recta ahí inventaría un movimiento continuo que no
        // ocurrió.
        step: 'end',
        // Sin símbolos: la serie es una consigna escalonada, no una nube de mediciones.
        showSymbol: false,
        lineStyle: { width: 1.75, color: METRIC_COLOR },
        itemStyle: { color: METRIC_COLOR },
        areaStyle: { color: METRIC_COLOR, opacity: 0.18 },
        data: positions,
        // La posición solicitada, que es contra lo que se lee el avance.
        markLine:
          requestedPosition == null
            ? undefined
            : {
                silent: true,
                symbol: 'none',
                lineStyle: { color: AU_SUCCESS, type: 'dashed', width: 1.4 },
                label: {
                  show: true,
                  position: 'insideEndTop',
                  formatter: t(i18n)`solicitada ${requestedValue}`,
                  color: AU_SUCCESS,
                  fontSize: 9,
                },
                data: [{ yAxis: requestedPosition }],
              },
        tooltip: {
          valueFormatter(value) {
            if (value !== undefined && typeof value === 'number') return formatPositionValue(value);
            else return '';
          },
        },
      },
    };
    /**
     * **Todas las dependencias son estables**: los dos formatters son `useCallback` sin deps que
     * leen una ref, y el resto son números. Así `option` sólo cambia de identidad cuando cambia un
     * dato de verdad —un punto nuevo, el tick de cinco segundos del eje— y no en cada render.
     *
     * Importa más que un `useMemo` cualquiera: el `useEffect` de `features/echarts` corre un
     * `setOption(..., { notMerge: true })`, o sea que **cada cambio de identidad de `option`
     * redibuja el gráfico entero** y reinicia su animación. Ése es el «se re-renderiza a cada rato»
     * que se veía.
     */
  }, [
    movement.positions,
    formatPositionValue,
    formatAxisTime,
    max,
    min,
    yMin,
    yMax,
    requestedPosition,
    i18n,
  ]);

  if (movement.positions.length === 0) {
    return (
      <Box display="grid" sx={{ placeItems: 'center', height: '100%', textAlign: 'center', p: 1 }}>
        <Typography sx={{ fontSize: 12, fontStyle: 'italic', color: AU_TEXT_DIM }}>
          <Trans>Cuando se reciban mensajes de posición se graficarán acá</Trans>
        </Typography>
      </Box>
    );
  }

  return <ECharts option={option} />;
};
